import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import {
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
} from "reactstrap";
import SalesReportService from "../../../services/OrderingServices/SalesReportService";
import CostCenterService from "../../../services/OrderingServices/CostCenterService";
import toast from "react-hot-toast";

const ReturnBill = ({
  show,
  onCloseClick,

  selectedItem,
  fetchCustomers,
  setSelectedItem,
}) => {
  const socketRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [userInfo, setUserInfo] = useState(null);
  const [webSocketUrl, setWebSocketUrl] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [costCenterData, setCostCenterData] = useState(null);
  const [remarks, setRemarks] = useState();
  const [touched, setTouched] = useState();
  const isInvalid = touched && !remarks?.trim();
  const [pan, setPan] = useState();
  const fetchCostCenter = async () => {
    try {
      const response = await CostCenterService.getList();
      const actualData = Array.isArray(response)
        ? response
        : response?.data || [];
      const billPrinter = actualData.find(
        (item) => item.name?.trim().toLowerCase() === "bill printer"
      );
      setCostCenterData(billPrinter || null);
    } catch (error) {
      console.error("❌ Fetch error:", error);
    }
  };

  useEffect(() => {
    fetchCostCenter();
  }, []);

  useEffect(() => {
    if (!costCenterData?.printerPort) return;

    const storedUserInfo = localStorage.getItem("user");
    if (storedUserInfo) {
      const parsedUserInfo = JSON.parse(storedUserInfo);
      setUserInfo(parsedUserInfo);

      const dynamicWebSocketUrl = `ws://localhost:${costCenterData.printerPort}`;
      setWebSocketUrl(dynamicWebSocketUrl);
    }
  }, [costCenterData]);

  useEffect(() => {
    if (!webSocketUrl) return;

    const connectWebSocket = () => {
      setConnectionStatus("connecting");
      socketRef.current = new WebSocket(webSocketUrl);

      socketRef.current.onopen = () => {
        console.log("WebSocket connection established");

        setConnectionStatus("connected");
      };

      socketRef.current.onclose = () => {
        console.log("WebSocket connection closed");

        setConnectionStatus("disconnected");
      };

      socketRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);

        setConnectionStatus("error");
        toast.error("WebSocket error. Please refresh.", {
          duration: 4000,
          position: "top-right",
          style: {
            background: "#fee2e2",
            color: "#b91c1c",
          },
        });
      };

      socketRef.current.onmessage = (event) => {
        try {
          const response = JSON.parse(event.data);
          if (response.status === "success") {
            toast.success(response.message || "Bill printed successfully", {
              duration: 4000,
              position: "top-right",
              style: {
                background: "#dcfce7",
                color: "#166534",
              },
            });
          }
        } catch (err) {
          console.error("WebSocket parse error:", err);
        } finally {
          setIsPrinting(false);
        }
      };
    };

    connectWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [webSocketUrl]);

  const handlePrint = (printData) => {
    console.log("🚀 ~ handlePrint ~ printData:", printData);
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      setIsPrinting(true);

      const formattedItems = printData?.returnItems?.map((item) => ({
        itemName: item.itemName,
        quantity: item.quantity,
        unitPrice: item.price,
        totalPrice: item.totalPrice,
        isComplimentary: false, // assuming no complimentary in return
      }));

      const billData = {
        cafeName: userInfo?.organizationInfo?.name,
        location: userInfo?.organizationInfo?.address1,
        type: "PRINT_BILL",
        phone: userInfo?.organizationInfo?.tel,
        billprintedby: userInfo?.userName,
        items: formattedItems,
        subTotal: printData?.totalAmount,
        netAmount: printData?.grandTotal,
        discount: printData?.discountAmount,
        // discountPercent: printData?.discountPercentage,
        refSalesInvoiceNumber: printData?.refSalesInvoiceNumber,
        returnInvoiceNumber: printData?.returnInvoiceNumber,
        customerPan: printData?.customerPan,
        message: "Thank you for visiting!",
        billremarks: remarks,
      };

      socketRef.current.send(JSON.stringify(billData));
    } else {
      console.error("WebSocket not connected");
      setConnectionStatus("error");
      toast.error("WebSocket not connected. Please try again.", {
        duration: 4000,
        position: "top-right",
        style: {
          background: "#fee2e2",
          color: "#b91c1c",
        },
      });
    }
  };

  const handleReturn = async () => {
    if (!remarks?.trim()) {
      setTouched(true);
      return;
    }

    if (selectedItem) {
      try {
        const response = await SalesReportService.returnBill({
          refSalesInvoiceNumber: selectedItem?.invoiceNumber,
          customerPan: pan,
          totalReturnAmount: selectedItem?.grandTotal,
          remarks,
        });

        toast.success("Selected bill is returned successfully");

        handlePrint(response); // ✅ Print after successful return
      } catch (error) {
        toast.error("Failed to return the bill");
      } finally {
        onCloseClick();
        setSelectedItem(null);
      }
    }
  };

  return (
    <Modal isOpen={show} toggle={onCloseClick}>
      <ModalBody className="py-3 px-5">
        <div className="mt-2 text-center">
          <lord-icon
            src="https://cdn.lordicon.com/gsqxdxog.json"
            trigger="loop"
            colors="primary:#f7b84b,secondary:#f06548"
            style={{ width: "100px", height: "100px" }}
          ></lord-icon>
          <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
            <h4>Are you sure?</h4>
            <p className="text-muted mx-4 mb-0">
              Are you sure you want to cancel this order?
            </p>
          </div>
          <FormGroup className="w-100 text-start">
            <Label for="pan" className="fw-medium">
              Pan number
            </Label>
            <Input
              id="pan"
              type="text"
              placeholder="Enter pan number"
              value={pan}
              onChange={(e) => setPan(e.target.value)}
              onBlur={() => setTouched(true)}
              invalid={isInvalid}
            />
          </FormGroup>
          <FormGroup className="w-100 text-start">
            <Label for="remarks" className="fw-medium">
              Remarks <span className="text-danger">*</span>
            </Label>
            <Input
              id="remarks"
              type="textarea"
              rows={4}
              placeholder="Enter remarks..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              onBlur={() => setTouched(true)}
              invalid={isInvalid}
            />
            <FormFeedback>Remarks is required.</FormFeedback>
          </FormGroup>
        </div>
        <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
          <button
            type="button"
            className="btn w-sm btn-light"
            onClick={onCloseClick}
          >
            Close
          </button>
          <button
            type="button"
            className="btn w-sm btn-danger"
            onClick={handleReturn}
            disabled={isPrinting || connectionStatus === "connecting"}
          >
            {isPrinting ? "Printing..." : "Yes, Return It!"}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

ReturnBill.propTypes = {
  show: PropTypes.any,
  onCloseClick: PropTypes.func,
  setPan: PropTypes.func,
  setRemarks: PropTypes.func,
  remarks: PropTypes.string,
  pan: PropTypes.string,
  isInvalid: PropTypes.bool,
  setTouched: PropTypes.func,
  selectedItem: PropTypes.object,
  fetchCustomers: PropTypes.func,
  setSelectedItem: PropTypes.func,
};

export default ReturnBill;
